import { Checkbox, Form, InputNumber, Skeleton } from 'antd'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'

import { restClient } from '@/Core/Services/RestClientService'
import { FormActionsBar } from '@/Ship/Components/UI/FormActionsBar'
import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'
import {
    DEFAULT_MILEAGE_OPTIONS,
    OPTION_MILEAGE_CONTROL_ENABLED,
    OPTION_MILEAGE_DEVIATION_KM,
    OPTION_MILEAGE_DEVIATION_PERCENT,
    type MileageOptionsForm,
    readMileageOptions,
} from '@/Ship/Helpers/portalOptions'

/**
 * Розділ «Контроль пробігу»: вмикач і пороги, за якими розбіжність пробігу за одометром і за Wialon
 * стає контрольним відхиленням (обидва одразу). Нові значення діють для наступних закриттів і правок —
 * уже оцінені зміни не змінюються, а пропущені на час вимкнення не дораховуються.
 */

const CODES = [OPTION_MILEAGE_CONTROL_ENABLED, OPTION_MILEAGE_DEVIATION_PERCENT, OPTION_MILEAGE_DEVIATION_KM] as const
export const MileageSection = observer(() => {
    const [form] = Form.useForm<MileageOptionsForm>()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [baseline, setBaseline] = useState<MileageOptionsForm | null>(null)
    const [values, setValues] = useState<MileageOptionsForm | null>(null)
    const dirty = baseline !== null && values !== null && CODES.some((code) => values[code] !== baseline[code])
    const enabled = values?.[OPTION_MILEAGE_CONTROL_ENABLED] ?? true

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const result: any = await restClient.get('/services/tenant/options', { searchParams: { limit: 300 } })
            const initial = readMileageOptions(result?.items ?? [])
            setBaseline(initial)
            setValues(initial)
        } catch {
            notify.error('Не вдалося завантажити налаштування')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const handleReset = () => {
        if (!baseline) return
        form.setFieldsValue(baseline)
        setValues(baseline)
    }

    const handleSave = async (formValues: MileageOptionsForm) => {
        setSaving(true)
        try {
            await Promise.all(
                CODES.map((code) =>
                    restClient.post('/services/tenant/options', {
                        json: {
                            code,
                            value:
                                code === OPTION_MILEAGE_CONTROL_ENABLED ? !!formValues[code] : Number(formValues[code]),
                        },
                    })
                )
            )
            setBaseline(formValues)
            notify.success('Налаштування збережено')
        } catch {
            notify.error('Помилка збереження налаштувань')
        } finally {
            setSaving(false)
        }
    }

    const head = (
        <div className={cx(shell, 'sectionHead')}>
            <h2 className={cx(shell, 'sectionTitle')}>Контроль пробігу</h2>
            <p className={cx(shell, 'sectionHint')}>
                Коли пробіг за одометром розходиться з пробігом за Wialon більше, ніж на обидва пороги одразу, зміна
                отримує контрольне відхилення для перевірки диспетчером.
            </p>
        </div>
    )

    if (loading || !baseline) {
        return (
            <div className={cx(shell, 'section')}>
                {head}
                <Skeleton active paragraph={{ rows: 3 }} />
            </div>
        )
    }

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={baseline}
            onValuesChange={(_, all) => setValues(all)}
            onFinish={handleSave}
        >
            <div className={cx(shell, 'stack')}>
                <div className={cx(shell, 'section')}>
                    {head}
                    <Form.Item
                        name={OPTION_MILEAGE_CONTROL_ENABLED}
                        valuePropName="checked"
                        extra={
                            enabled
                                ? 'Закриття зміни й правка одометрів чи пробігу Wialon перевіряють розбіжність'
                                : 'Нові закриття й правки не перевіряються. Уже оцінені зміни лишаються, їх можна розглянути; пропущені після ввімкнення не дораховуються'
                        }
                    >
                        <Checkbox data-testid="mileage-control-enabled">Контроль пробігу ввімкнено</Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={OPTION_MILEAGE_DEVIATION_PERCENT}
                        label="Поріг розбіжності, %"
                        rules={[{ required: true, message: 'Обовʼязкове поле' }]}
                        extra={`Від пробігу за Wialon. За замовчуванням — ${DEFAULT_MILEAGE_OPTIONS[OPTION_MILEAGE_DEVIATION_PERCENT]}%`}
                    >
                        <InputNumber min={0} max={1000} step={1} style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item
                        name={OPTION_MILEAGE_DEVIATION_KM}
                        label="Поріг розбіжності, км"
                        rules={[{ required: true, message: 'Обовʼязкове поле' }]}
                        extra={`Щоб кілька кілометрів на короткій зміні не ставали відхиленням. За замовчуванням — ${DEFAULT_MILEAGE_OPTIONS[OPTION_MILEAGE_DEVIATION_KM]} км`}
                        style={{ marginBottom: 0 }}
                    >
                        <InputNumber min={0} max={100000} step={5} precision={0} style={{ width: 200 }} />
                    </Form.Item>
                </div>

                <div className={cx(shell, 'actions')}>
                    <FormActionsBar dirty={dirty} saving={saving} onReset={handleReset} />
                </div>
            </div>
        </Form>
    )
})
